import { Formik } from 'formik'
import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import * as Yup from 'yup'
import TextField from 'features/forms/components/TextField'
import FullPageLoading from 'components/FullPageLoading'
import Button from 'components/Button'
import { _ } from 'utils'
import { QueryResponse } from 'utils/graphql'
import SubmitButton from 'features/forms/components/SubmitButton'
import { showNotification } from 'features/notifications'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
import { addMeetingParticipant } from 'features/meetings/graphql/mutation'
import { AddMeetingUser, RoleChoices } from 'generated/graphql'
import RadioButtonField from 'features/forms/components/RadioButtonField'

const S = {
    Actions: styled.div`
        display: flex;
        justify-content: space-between;
        width: 100%;
        padding-right: 10px;
    `,
}

interface InviteFormProps {
    close: () => void
    onSubmit?: (email: string) => Promise<QueryResponse<any>>
    meetingId: number
    fetchParticipants?: () => void
    fetchHosts?: () => void
}

const EmailValidation = Yup.object().shape({
    email: Yup.string().email().required()
})

const InviteParticipantForm = ({
    close,
    meetingId,
    fetchParticipants,
    fetchHosts,
}: InviteFormProps) => {
    const dispatch = useDispatch()
    const [isUploading, setIsUploading] = useState(false)

    const handleSubmit = async (values: AddMeetingUser) => {
        setIsUploading(true)
        const response = await addMeetingParticipant({ ...values, meetingId: meetingId.toString() })
        setIsUploading(false)

        if (response.errors.find((e) => e.includes('duplicate key value violates unique constraint'))) {
            dispatch(showErrorNotification(_('meeting.form.duplicated_participant_email')))
            return
        }

        if (!response.data || response.errors.length) {
            dispatch(showErrorNotification(response.errors))
            return
        }

        values.role === RoleChoices.Audience
            ? fetchParticipants()
            : fetchHosts()

        dispatch(showNotification({
            type: 'success',
            body: _('meeting_participant.add_successfully')
        }))
        close()
    }

    return (
        <Formik
            initialValues={{
                email: ''
            } as AddMeetingUser}
            onSubmit={handleSubmit}
            validationSchema={EmailValidation}
        >
            {({ submitForm }) => (
                <>
                    <Form
                        className="row"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                submitForm()
                            }
                        }}
                    >
                        <TextField
                            name="email"
                            label={_('form.email_address')}
                            isRequired
                        />
                        <RadioButtonField
                            name="role"
                            label={_('meeting_participant.role.label')}
                            options={[
                                {
                                    label: _('meeting_participant.role.audience'),
                                    value: RoleChoices.Audience
                                },
                                {
                                    label: _('meeting_participant.role.speaker'),
                                    value: RoleChoices.Speaker
                                },
                                {
                                    label: _('meeting_participant.role.primary_speaker'),
                                    value: RoleChoices.PrimarySpeaker
                                },
                                {
                                    label: _('meeting_participant.role.moderator'),
                                    value: RoleChoices.Moderator
                                },
                            ]}
                            defaultValue={RoleChoices.Audience}
                            isVerticalAlign
                        />
                        <S.Actions>
                            <div />
                            <div className="d-flex">
                                <Button variant="secondary" onClick={close}>{_('button.cancel')}</Button>
                                <SubmitButton />
                            </div>
                        </S.Actions>
                    </Form>
                    {isUploading && <FullPageLoading isAbsolute fullWidth />}
                </>
            )}
        </Formik>
    )
}

export default InviteParticipantForm
